/* eslint-disable no-console */
import { ReactElement, useContext, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { FocusError } from 'focus-formik-error';
import { AdaButton } from '@betrybe/ada-react';
import { DefaultLayout } from '@/layouts/index';
import getAddress from '@/services/cep';
import { useCandidate } from '@/services/pre-enrollment';
import { StudentContext, useToastNotification } from '@/contexts';
import useLazyTrack from '@/hooks/tracking/useLazyTrack';
import { ActorTypeEnum, SourceEnum, VerbEnum } from '@/tracker/enums';
import { checkErrors } from '@/utils';
import { useClient, useStateMachine } from '@/hooks';
import LayoutDescription from './components';
import BasicInformationForm from './form-components/basic-information-form';
import AddressForm from './form-components/address-form';
import DocumentsForm from './form-components/documents-form';
import AdditionalInformationForm from './form-components/additional-information-form';
import fileUpload from '@/utils/file-upload-function/fileUpload';
import {
  initialFormValues,
  cadidateFormValidationSchema,
  mountRegisterObj,
} from './utils';
import StatesEnum from '@/utils/enums/StatesEnum';

function Register(): ReactElement {
  const [, toastNotificationDispatch] = useToastNotification();
  const { state } = useContext(StudentContext);
  const {
    clientId,
    currentState,
    candidateApplicationId,
    processInfo,
    selectionProcessId,
    processDateInfo,
    stateMachineVersion,
    setActualState,
  } = useClient();
  const nationalityRef = useRef<null | HTMLDivElement>(null);
  const cpfFileRef = useRef<null | HTMLDivElement>(null);
  const rgFileRef = useRef<null | HTMLDivElement>(null);
  const proofOfAddressRef = useRef<null | HTMLDivElement>(null);
  const selfieWithDocumentRef = useRef<null | HTMLDivElement>(null);

  const [proofOfAddressImageURL, setProofOfAddressImageURL] =
    useState<string>('');
  const [cpfFileImageURL, setCpfFileImageURL] = useState<string>('');
  const [rgFileImageURL, setRgFileImageURL] = useState<string>('');
  const [selfieImageURL, setSelfieImageURL] = useState<string>('');
  const history = useHistory();
  const track = useLazyTrack();
  const { registerCandidate } = useCandidate();
  const { getStateObject } = useStateMachine();

  const formik = useFormik({
    initialValues: initialFormValues(
      state.clientData,
      state.paymentMethod.toLowerCase(),
    ),
    validationSchema: Yup.object().shape(cadidateFormValidationSchema),
    onSubmit: async (values, { setFieldValue }) => {
      const birth = new Date(`${values.year}-${values.month}-${values.day}`);
      await setFieldValue('birthdate', birth);

      const params = mountRegisterObj({
        values,
        candidateApplicationId,
        cpfFileImageURL,
        proofOfAddressImageURL,
        rgFileImageURL,
        selectionProcessId,
        selfieImageURL,
      });

      registerCandidate(clientId, params)
        .then(async (response) => {
          track(
            'Financing Request Personal Information Submitted',
            {
              actor: {
                user_id: clientId,
                type: ActorTypeEnum.CANDIDATE,
                role: '',
              },
              object: {
                path: window.location.pathname,
                url: window.location.href,
                component: 'Prosseguir',
                params,
                process_info: processInfo,
              },
              context: {
                source: SourceEnum.FINANCING_REQUEST,
              },
              verb: VerbEnum.CLICK,
            },
            'track',
          );
          if (await response.data) {
            if (currentState === StatesEnum.SCORE_REGISTRATION_COMPLETED) {
              history.push('/register/guarantors');
            }
            const stateObj = getStateObject({
              state: currentState,
              version: stateMachineVersion,
            });
            if (stateObj && stateObj.nextState) {
              await setActualState(stateObj.nextState);
              history.push(stateObj.nextUrl);
            }
          }
        })
        .catch((error) => {
          if (error) {
            toastNotificationDispatch({
              type: 'error',
              payload: {
                title: 'Erro ao realizar cadastro',
                text: 'Por algum motivo seu cadastro não pode ser realizado, verifique se todos os dados estão preenchidos corretamente.',
              },
            });
            const payload = {
              userId: clientId,
              currentState,
            };
            Sentry.setUser({ id: clientId });
            Sentry.captureException({ error, payload });
          }
        });
    },
  });

  async function handleSearchCep(cep: string) {
    const address = await getAddress(cep);
    formik.setValues((v) => ({
      ...v,
      postCode: address.cep,
      city: address.localidade,
      address: address.logradouro,
      state: address.uf,
      neighborhood: address.bairro,
    }));
  }

  const documentOwner = 'client';

  return (
    <DefaultLayout
      title="Cadastre seus dados"
      description={
        <LayoutDescription
          text="Lembre-se de ter em mãos seus documentos digitalizados: RG, CPF,
                comprovante de endereço e selfie com seu documento."
          color="primary"
        />
      }
      step="1 de 4"
      progressPercentage={25}
      limitDate={
        processDateInfo?.limitDateFinancingRequest ||
        state.processDateInfo?.limitDateFinancingRequest
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <FocusError formik={formik} />

        <div className="flex flex-col my-10">
          <BasicInformationForm
            formik={formik}
            nationalityRef={nationalityRef}
            formType="candidate"
          />
          <AddressForm
            formik={formik}
            // eslint-disable-next-line react/jsx-no-bind
            handleSearchCep={handleSearchCep}
            handleFileUpload={(event, field, fieldName) => {
              fileUpload({
                event,
                field,
                fieldName,
                formik,
                documentOwner,
                clientId,
                setProofOfAddressImageURL,
                setCpfFileImageURL,
                setRgFileImageURL,
                setSelfieImageURL,
              });
            }}
            proofOfAddressRef={proofOfAddressRef}
            formType="candidate"
          />
          <DocumentsForm
            formik={formik}
            cpfFileRef={cpfFileRef}
            rgFileRef={rgFileRef}
            selfieWithDocumentRef={selfieWithDocumentRef}
            handleFileUpload={(event, field, fieldName) => {
              fileUpload({
                event,
                field,
                fieldName,
                formik,
                documentOwner,
                clientId,
                setProofOfAddressImageURL,
                setCpfFileImageURL,
                setRgFileImageURL,
                setSelfieImageURL,
              });
            }}
          />
          <AdditionalInformationForm formik={formik} formType="candidate" />
        </div>

        <AdaButton
          id="button-register-submit"
          type="submit"
          onClick={async () => {
            const error = await formik.validateForm();
            checkErrors({
              errors: error,
              nationalityRef,
              cpfFileRef,
              rgFileRef,
              proofOfAddressRef,
              selfieWithDocumentRef,
            });
          }}
        >
          Prosseguir
        </AdaButton>
      </form>
    </DefaultLayout>
  );
}

export default Register;
